import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import userService from "../../utils/userService";
import authService from "../../utils/authService";

// Actions asynchrones pour récupérer les données utilisateur
export const fetchUserProfile = createAsyncThunk(
  "userProfile/fetchProfile",
  async (userId, { rejectWithValue }) => {
    console.log("fetchUserProfile called with userId:", userId);
    try {
      console.log("Calling userService.getUserProfile with userId:", userId);
      const response = await userService.getUserProfile(userId);
      console.log("getUserProfile response:", response);
      return response.data;
    } catch (error) {
      console.error("fetchUserProfile error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération du profil",
      );
    }
  },
);

// Nouvelle action pour récupérer le profil de l'utilisateur connecté
export const fetchMyProfile = createAsyncThunk(
  "userProfile/fetchMyProfile",
  async (_, { rejectWithValue }) => {
    console.log("fetchMyProfile called");
    try {
      console.log("Calling userService.getMyProfile");
      const response = await userService.getMyProfile();
      console.log("getMyProfile response:", response);
      // L'intercepteur retourne déjà response.data, donc les données sont dans response.data
      return response.data;
    } catch (error) {
      console.error("fetchMyProfile error:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération du profil",
      );
    }
  },
);

export const fetchUserMissions = createAsyncThunk(
  "userProfile/fetchMissions",
  async ({ userId, type = "created", params = {} }, { rejectWithValue }) => {
    try {
      let response;
      if (type === "created") {
        response = await userService.getUserCreatedMissions(userId, params);
      } else if (type === "participated") {
        response = await userService.getUserParticipatedMissions(
          userId,
          params,
        );
      }
      return { type, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des missions",
      );
    }
  },
);

export const fetchUserMissionStats = createAsyncThunk(
  "userProfile/fetchMissionStats",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserMissionStats(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des statistiques",
      );
    }
  },
);

export const fetchUserApplications = createAsyncThunk(
  "userProfile/fetchApplications",
  async ({ userId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await userService.getUserApplications(userId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des candidatures",
      );
    }
  },
);

export const fetchUserApplicationStats = createAsyncThunk(
  "userProfile/fetchApplicationStats",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserApplicationStats(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des statistiques de candidatures",
      );
    }
  },
);

export const fetchUserMessages = createAsyncThunk(
  "userProfile/fetchMessages",
  async ({ userId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await userService.getUserMessages(userId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des messages",
      );
    }
  },
);

export const fetchUserNotifications = createAsyncThunk(
  "userProfile/fetchNotifications",
  async ({ userId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await userService.getUserNotifications(userId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des notifications",
      );
    }
  },
);

export const fetchUserCommunicationStats = createAsyncThunk(
  "userProfile/fetchCommunicationStats",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserCommunicationStats(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des statistiques de communication",
      );
    }
  },
);

// Sauvegarder les informations du profil de l'utilisateur connecté
export const saveMyProfile = createAsyncThunk(
  "userProfile/saveMyProfile",
  async ({ formData, availability }, { getState, rejectWithValue }) => {
    try {
      const isFormData = !!formData && typeof formData.get === "function";
      const getValue = (key) => {
        if (!formData) return undefined;
        return isFormData ? formData.get(key) : formData[key];
      };
      const getJsonValue = (key, fallback) => {
        const value = getValue(key);
        if (value === undefined || value === null || value === "")
          return fallback;
        if (typeof value === "string") {
          try {
            return JSON.parse(value);
          } catch {
            return fallback;
          }
        }
        return value;
      };
      const toSlug = (value) => {
        if (!value) return "";
        return value
          .toString()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/['’]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "")
          .replace(/_+/g, "_");
      };

      // 1) Mise à jour des informations basiques via /auth/updatedetails
      const basicUpdate = {
        firstName: getValue("firstName"),
        lastName: getValue("lastName"),
        email: getValue("email"),
        phone: getValue("phone"),
        bio: getValue("bio"),
        address: {
          city: getValue("city"),
          country: getValue("country"),
        },
        socialLinks: {
          website: getValue("website"),
        },
      };

      // Note: l'intercepteur axios renvoie déjà response.data
      const basicResp = await authService.updateDetails(basicUpdate);
      // backend: { success, message, data: { user } }
      const basicUser = basicResp?.data?.user;

      // 2) Mise à jour des données professionnelles via /users/:id
      const authUser = getState().auth.user;
      const userId = authUser?._id || authUser?.id;

      const availabilityFromBody =
        availability ?? getJsonValue("availability", undefined);

      // Mapper les disponibilités simples vers le schéma journalier (disponible true/false)
      const professionalAvailability = {
        monday: { available: !!availabilityFromBody?.weekdays },
        tuesday: { available: !!availabilityFromBody?.weekdays },
        wednesday: { available: !!availabilityFromBody?.weekdays },
        thursday: { available: !!availabilityFromBody?.weekdays },
        friday: { available: !!availabilityFromBody?.weekdays },
        saturday: { available: !!availabilityFromBody?.weekends },
        sunday: { available: !!availabilityFromBody?.weekends },
      };

      // Utiliser la notation par points pour éviter d'écraser l'objet complet 'professional'
      const professionalUpdate = {};
      if (
        getValue("hourlyRate") !== undefined &&
        getValue("hourlyRate") !== null &&
        getValue("hourlyRate") !== ""
      ) {
        professionalUpdate["professional.hourlyRate"] = Number(
          getValue("hourlyRate"),
        );
      }
      const specialties = getJsonValue("specialties", undefined);
      if (Array.isArray(specialties)) {
        professionalUpdate["professional.specialties"] = specialties;
      }
      professionalUpdate["professional.availability"] =
        professionalAvailability;

      const stateSkills = getState().userProfile.skills;
      if (Array.isArray(stateSkills)) {
        const normalizedSkills = stateSkills
          .filter((s) => s && (s.name || s.label))
          .map((s) => {
            const name = (s.name || s.label || "").toString().trim();
            const slug = (s.slug || toSlug(name)).toString();
            return {
              name,
              slug,
              level: s.level || "Intermédiaire",
              category: s.category || "Autre",
              needsAdminValidation: !!s.needsAdminValidation,
            };
          })
          .filter((s) => s.name);

        professionalUpdate["professional.skills"] = normalizedSkills;
        professionalUpdate["professional.skillSlugs"] = Array.from(
          new Set(normalizedSkills.map((s) => s.slug).filter(Boolean)),
        );
      }

      const stateCertifications = getState().userProfile.certifications;
      if (Array.isArray(stateCertifications)) {
        const normalizedCertifications = stateCertifications
          .filter((c) => c && (c.name || c.label))
          .map((c) => {
            const name = (c.name || c.label || "").toString().trim();
            const slug = (c.slug || toSlug(name)).toString();
            return {
              name,
              slug,
              discipline: c.discipline || "",
              level: c.level || "",
              needsAdminValidation: !!c.needsAdminValidation,
              issuer: c.issuer || c.organization || "",
            };
          })
          .filter((c) => c.name);

        professionalUpdate["professional.certifications"] =
          normalizedCertifications;
      }

      let professionalResp = null;
      if (userId) {
        professionalResp = await userService.updateUser(
          userId,
          professionalUpdate,
        );
      }

      // backend: { success, data: user }
      const professionalUser =
        professionalResp?.data || professionalResp?.data?.data;

      // Fusionner les réponses pour renvoyer un profil consolidé
      const mergedUser = { ...(professionalUser || basicUser) };

      return mergedUser;
    } catch (error) {
      // L'intercepteur axios renvoie une Error avec message; préférer error.message
      const message =
        error?.message ||
        error?.response?.data?.message ||
        "Erreur lors de la sauvegarde du profil";
      return rejectWithValue(message);
    }
  },
);

// Mock data pour le profil utilisateur connecté
const mockUserProfile = {
  id: 1,
  firstName: "Jean",
  lastName: "Dupont",
  email: "jean.dupont@email.com",
  phone: "+33 6 12 34 56 78",
  type: "professional", // 'professional' ou 'organization'
  avatar: "/default-avatar.svg",
  bio: "Entraîneur de football passionné avec plus de 10 ans d'expérience dans le sport amateur et professionnel. Spécialisé dans la formation des jeunes talents et la préparation physique.",
  city: "Paris",
  country: "France",
  location: "Paris, France",
  website: "www.jean-dupont-coach.fr",
  joinDate: "2022-03-15",
  isVerified: true,
  rating: 4.8,
  totalReviews: 24,
  completedMissions: 18,
  // Nouvelles données pour l'édition du profil
  hourlyRate: 45,
  dailyRate: 300,
  currency: "EUR",
  availability: {
    weekdays: true,
    weekends: true,
    evenings: false,
    mornings: true,
    afternoons: true,
  },
  languages: ["Français", "Anglais"],
  experience: "10+ ans",
  specialties: ["Formation jeunes", "Préparation physique", "Coaching mental"],
};

const mockSkills = [
  { id: 1, name: "Football", level: "Expert", experience: "10+ ans" },
  { id: 2, name: "Entraînement", level: "Expert", experience: "8 ans" },
  { id: 3, name: "Préparation physique", level: "Avancé", experience: "5 ans" },
  {
    id: 4,
    name: "Nutrition sportive",
    level: "Intermédiaire",
    experience: "3 ans",
  },
  {
    id: 5,
    name: "Psychologie du sport",
    level: "Débutant",
    experience: "1 an",
  },
];

const mockCertifications = [
  {
    id: 1,
    name: "Diplôme d'entraîneur FFF",
    issuer: "Fédération Française de Football",
    date: "2020-06-15",
    verified: true,
  },
  {
    id: 2,
    name: "Formation Premiers Secours",
    issuer: "Croix-Rouge Française",
    date: "2023-01-20",
    verified: true,
  },
  {
    id: 3,
    name: "Certificat Préparation Physique",
    issuer: "INSEP",
    date: "2021-09-10",
    verified: false,
  },
];

const mockUserMissions = [
  {
    id: 1,
    title: "Entraîneur équipe U15",
    organization: "FC Local Paris",
    status: "completed",
    startDate: "2024-01-15",
    endDate: "2024-03-15",
    rating: 5,
    payment: 1200,
    description: "Entraînement hebdomadaire équipe U15",
  },
  {
    id: 2,
    title: "Stage de football été",
    organization: "Club Sportif de Vincennes",
    status: "in_progress",
    startDate: "2024-07-01",
    endDate: "2024-07-31",
    rating: null,
    payment: 2500,
    description: "Animation stage été pour enfants 8-12 ans",
  },
  {
    id: 3,
    title: "Préparation physique équipe senior",
    organization: "AS Boulogne",
    status: "applied",
    startDate: "2024-09-01",
    endDate: "2024-12-31",
    rating: null,
    payment: 3000,
    description: "Préparation physique équipe senior championnat régional",
  },
];

const mockEvaluations = [
  {
    id: 1,
    rating: 5,
    comment:
      "Excellent entraîneur ! Très professionnel et à l'écoute des joueurs. Les résultats ont été remarquables.",
    author: "Marie Dubois",
    authorRole: "Présidente FC Local Paris",
    date: "2024-03-20",
    missionTitle: "Entraîneur équipe U15",
  },
  {
    id: 2,
    rating: 5,
    comment:
      "Jean a su motiver notre équipe et améliorer significativement notre niveau de jeu. Recommandé !",
    author: "Pierre Martin",
    authorRole: "Directeur sportif",
    date: "2024-02-10",
    missionTitle: "Formation technique jeunes",
  },
  {
    id: 3,
    rating: 4,
    comment:
      "Très bon travail sur la préparation physique. Quelques points d'amélioration sur la communication.",
    author: "Sophie Laurent",
    authorRole: "Entraîneur principal",
    date: "2024-01-15",
    missionTitle: "Assistant préparation physique",
  },
];

const mockMessages = [
  {
    id: 1,
    sender: "Club Sportif Lyon",
    subject: "Nouvelle mission disponible",
    preview: "Nous avons une mission qui pourrait vous intéresser...",
    date: "2024-01-20",
    unread: true,
  },
  {
    id: 2,
    sender: "AS Marseille",
    subject: "Confirmation mission",
    preview: "Votre candidature a été acceptée pour...",
    date: "2024-01-18",
    unread: false,
  },
  {
    id: 3,
    sender: "FC Toulouse",
    subject: "Évaluation mission",
    preview: "Merci de bien vouloir évaluer votre dernière mission...",
    date: "2024-01-15",
    unread: false,
  },
];

const mockSettings = {
  notifications: {
    email: true,
    sms: false,
    push: true,
    newMissions: true,
    messages: true,
    evaluations: true,
  },
  privacy: {
    profileVisible: true,
    showEmail: false,
    showPhone: true,
  },
  preferences: {
    language: "fr",
    timezone: "Europe/Paris",
    currency: "EUR",
  },
};

const initialState = {
  profile: null,
  skills: [],
  certifications: [],
  missions: {
    created: [],
    participated: [],
  },
  missionStats: null,
  applications: [],
  applicationStats: null,
  evaluations: [],
  messages: [],
  notifications: [],
  communicationStats: null,
  settings: {
    notifications: {
      email: true,
      sms: false,
      push: true,
      newMissions: true,
      messages: true,
      evaluations: true,
    },
    privacy: {
      profileVisible: true,
      showEmail: false,
      showPhone: true,
    },
    preferences: {
      language: "fr",
      timezone: "Europe/Paris",
      currency: "EUR",
    },
  },
  loading: {
    profile: false,
    missions: false,
    applications: false,
    messages: false,
    notifications: false,
    stats: false,
  },
  error: null,
  editMode: false,
};

const userProfileSlice = createSlice({
  name: "userProfile",
  initialState,
  reducers: {
    setProfile: (state, action) => {
      state.profile = { ...state.profile, ...action.payload };
    },
    updateProfileData: (state, action) => {
      state.profile = { ...state.profile, ...action.payload };
    },
    addSkill: (state, action) => {
      const newSkill = {
        id: Date.now(),
        ...action.payload,
      };
      state.skills.push(newSkill);
    },
    updateSkill: (state, action) => {
      const { id, ...updates } = action.payload;
      const skillIndex = state.skills.findIndex((skill) => skill.id === id);
      if (skillIndex !== -1) {
        state.skills[skillIndex] = { ...state.skills[skillIndex], ...updates };
      }
    },
    removeSkill: (state, action) => {
      state.skills = state.skills.filter(
        (skill) => skill.id !== action.payload,
      );
    },
    addCertification: (state, action) => {
      const newCertification = {
        id: Date.now(),
        ...action.payload,
      };
      state.certifications.push(newCertification);
    },
    updateMissionStatus: (state, action) => {
      const { missionId, status } = action.payload;
      const missionIndex = state.missions.findIndex(
        (mission) => mission.id === missionId,
      );
      if (missionIndex !== -1) {
        state.missions[missionIndex].status = status;
      }
    },
    markMessageAsRead: (state, action) => {
      const messageIndex = state.messages.findIndex(
        (message) => message.id === action.payload,
      );
      if (messageIndex !== -1) {
        state.messages[messageIndex].unread = false;
      }
    },
    updateSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    updateNotificationSettings: (state, action) => {
      state.settings.notifications = {
        ...state.settings.notifications,
        ...action.payload,
      };
    },
    setEditMode: (state, action) => {
      state.editMode = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetProfile: (state) => {
      state.profile = mockUserProfile;
      state.skills = mockSkills;
      state.certifications = mockCertifications;
      state.editMode = false;
      state.error = null;
    },
    removeCertification: (state, action) => {
      state.certifications = state.certifications.filter(
        (cert) => cert.id !== action.payload,
      );
    },
    updateAvailability: (state, action) => {
      state.profile.availability = {
        ...state.profile.availability,
        ...action.payload,
      };
    },
    updateRates: (state, action) => {
      const { hourlyRate, dailyRate, currency } = action.payload;
      if (hourlyRate !== undefined) state.profile.hourlyRate = hourlyRate;
      if (dailyRate !== undefined) state.profile.dailyRate = dailyRate;
      if (currency !== undefined) state.profile.currency = currency;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload;
      })

      // Fetch My Profile (utilisateur connecté)
      .addCase(fetchMyProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(fetchMyProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.profile = action.payload;
        if (Array.isArray(action.payload?.professional?.skills)) {
          state.skills = action.payload.professional.skills.map((s, index) => ({
            id: s.id || index + 1,
            ...s,
          }));
        }
        if (Array.isArray(action.payload?.professional?.certifications)) {
          state.certifications = action.payload.professional.certifications.map(
            (c, index) => ({
              id: c.id || index + 1,
              ...c,
            }),
          );
        }
        state.error = null;
      })
      .addCase(fetchMyProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload;
      })

      // Fetch User Missions
      .addCase(fetchUserMissions.pending, (state) => {
        state.loading.missions = true;
        state.error = null;
      })
      .addCase(fetchUserMissions.fulfilled, (state, action) => {
        state.loading.missions = false;
        const { type, data } = action.payload;
        if (type === "created") {
          state.missions.created = data;
        } else if (type === "participated") {
          state.missions.participated = data;
        }
        state.error = null;
      })
      .addCase(fetchUserMissions.rejected, (state, action) => {
        state.loading.missions = false;
        state.error = action.payload;
      })

      // Fetch Mission Stats
      .addCase(fetchUserMissionStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchUserMissionStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.missionStats = action.payload;
        state.error = null;
      })
      .addCase(fetchUserMissionStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload;
      })

      // Fetch User Applications
      .addCase(fetchUserApplications.pending, (state) => {
        state.loading.applications = true;
        state.error = null;
      })
      .addCase(fetchUserApplications.fulfilled, (state, action) => {
        state.loading.applications = false;
        state.applications = action.payload;
        state.error = null;
      })
      .addCase(fetchUserApplications.rejected, (state, action) => {
        state.loading.applications = false;
        state.error = action.payload;
      })

      // Fetch Application Stats
      .addCase(fetchUserApplicationStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchUserApplicationStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.applicationStats = action.payload;
        state.error = null;
      })
      .addCase(fetchUserApplicationStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload;
      })

      // Fetch User Messages
      .addCase(fetchUserMessages.pending, (state) => {
        state.loading.messages = true;
        state.error = null;
      })
      .addCase(fetchUserMessages.fulfilled, (state, action) => {
        state.loading.messages = false;
        state.messages = action.payload;
        state.error = null;
      })
      .addCase(fetchUserMessages.rejected, (state, action) => {
        state.loading.messages = false;
        state.error = action.payload;
      })

      // Fetch User Notifications
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading.notifications = true;
        state.error = null;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.loading.notifications = false;
        state.notifications = action.payload;
        state.error = null;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.loading.notifications = false;
        state.error = action.payload;
      })

      // Fetch Communication Stats
      .addCase(fetchUserCommunicationStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchUserCommunicationStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.communicationStats = action.payload;
        state.error = null;
      })
      .addCase(fetchUserCommunicationStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload;
      })

      // Save My Profile
      .addCase(saveMyProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(saveMyProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        // Mettre à jour le profil avec les données renvoyées par le backend
        const user = action.payload;
        // Adapter quelques champs attendus par l'UI si présents dans professional
        const adaptedProfile = {
          ...user,
          hourlyRate:
            user?.professional?.hourlyRate ?? state.profile?.hourlyRate,
          availability:
            user?.professional?.availability ?? state.profile?.availability,
          specialties:
            user?.professional?.specialties ?? state.profile?.specialties,
        };
        state.profile = adaptedProfile;
        if (Array.isArray(user?.professional?.skills)) {
          state.skills = user.professional.skills.map((s, index) => ({
            id: s.id || index + 1,
            ...s,
          }));
        }
        if (Array.isArray(user?.professional?.certifications)) {
          state.certifications = user.professional.certifications.map(
            (c, index) => ({
              id: c.id || index + 1,
              ...c,
            }),
          );
        }
        state.error = null;
      })
      .addCase(saveMyProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload;
      });
  },
});

export const {
  setProfile,
  updateProfileData,
  addSkill,
  updateSkill,
  removeSkill,
  addCertification,
  removeCertification,
  updateMissionStatus,
  markMessageAsRead,
  updateSettings,
  updateNotificationSettings,
  setEditMode,
  setLoading,
  setError,
  clearError,
  resetProfile,
  updateAvailability,
  updateRates,
} = userProfileSlice.actions;

export default userProfileSlice.reducer;
